import { isConversationTurnStart } from '../../../shared/conversationTurns';
import { useEffect, useRef, useState } from 'react';
import { Dialog } from '@headlessui/react';
import type { CoworkMessage } from '../../types/cowork';
import { coworkService } from '../../services/cowork';
import { i18nService } from '../../services/i18n';

/** 按 user 消息选择完整轮次；请求失败保留选择，关闭或切会话后忽略迟到 UI 更新。 */
export default function ConversationTurnDeleteDialog({ sessionId, messages, blocked, onClose }: {
  sessionId: string; messages: CoworkMessage[]; blocked: boolean; onClose(): void;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const locked = useRef(false);
  const alive = useRef(true);
  const users = messages.filter(message => isConversationTurnStart(message));
  const currentIds = new Set(users.map(message => message.id));
  const chosen = [...selected].filter(id => currentIds.has(id));
  const t = (key: string) => i18nService.t(`turnDelete_${key}`);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  /** 二次确认后提交，使用同步锁阻止双击；不做乐观删除。 */
  const remove = async (): Promise<void> => {
    if (locked.current || blocked || !chosen.length || chosen.length > 200) return;
    if (!confirming) { setConfirming(true); return; }
    locked.current = true; setBusy(true); setError('');
    try {
      await coworkService.deleteTurns(sessionId, chosen);
      if (alive.current) onClose();
    } catch (cause) {
      if (alive.current) {
        const code = cause instanceof Error ? cause.message : '';
        setError(t(code.includes('SESSION_BUSY') ? 'busy' : code.includes('HISTORY_RESET_UNSUPPORTED') ? 'unsupported' : 'failed'));
      }
    } finally {
      locked.current = false;
      if (alive.current) setBusy(false);
    }
  };
  return <Dialog open onClose={() => { if (!locked.current) onClose(); }} className="relative z-[150]">
    <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
    <div className="fixed inset-0 flex items-center justify-center p-3">
      <Dialog.Panel className="flex max-h-[90dvh] w-full max-w-2xl flex-col gap-3 rounded-xl bg-claude-bg dark:bg-claude-darkBg p-4 text-claude-text dark:text-claude-darkText">
        <Dialog.Title className="font-semibold">{t('title')}</Dialog.Title>
        <Dialog.Description className="text-sm opacity-70">{t('hint')}</Dialog.Description>
        {(blocked || error) && <p role="alert" className="text-sm text-red-500">{blocked ? t('busy') : error}</p>}
        <div className="min-h-0 overflow-y-auto space-y-2">
          {users.map((message, index) => <label key={message.id} className="flex cursor-pointer items-start gap-3 rounded-lg border border-claude-border dark:border-claude-darkBorder p-3 text-sm">
            <input type="checkbox" className="mt-1" checked={selected.has(message.id)} disabled={busy || blocked || (!selected.has(message.id) && chosen.length >= 200)}
              onChange={event => {
                const checked = event.target.checked;
                setSelected(previous => { const next = new Set(previous); if (checked) next.add(message.id); else next.delete(message.id); return next; });
                setConfirming(false);
              }} />
            <span className="min-w-0 flex-1 break-words"><span className="opacity-60">{index + 1}. </span>{message.content.slice(0, 240) || t('attachment')}{message.content.length > 240 ? '…' : ''}</span>
          </label>)}
        </div>
        <p className="text-xs opacity-70">{t('count').replace('{count}', String(chosen.length))}</p>
        {confirming && <p role="status" className="text-sm text-red-500">{t('confirm')}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" disabled={busy} onClick={onClose} className="rounded-lg border px-3 py-2 text-sm disabled:opacity-40">{i18nService.t('cancel')}</button>
          <button type="button" disabled={busy || blocked || !chosen.length} onClick={() => void remove()} className="rounded-lg bg-red-600 px-3 py-2 text-sm text-white disabled:opacity-40">{t(busy ? 'deleting' : confirming ? 'confirmButton' : 'delete')}</button>
        </div>
      </Dialog.Panel>
    </div>
  </Dialog>;
}
